import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import BenchmarkingMedicaidService from "@app/overviews/network-benchmarking-dashboard/benchmarkingMedicaidService";
import BenchmarkingQHPService from "@app/overviews/network-benchmarking-dashboard/benchmarkingQHPService";
import {
  CompetitiveInsightsManageAddModalOptionsModel,
  CompetitiveInsightsManageAddModalProgramOption,
  CompetitiveInsightsManageAddModalProjectOption,
  CompetitiveInsightsManageAddModalStateOption,
} from "@app/overviews/network-benchmarking-dashboard/manage/shared/CompetitiveInsightsManageAddModalOptionsModel";

export interface CompetitiveInsightsManageAddModalConfigurationOptionsResult {
  isLoading: boolean;
  programs: CompetitiveInsightsManageAddModalProgramOption[];
  projects: CompetitiveInsightsManageAddModalProjectOption[];
  states: CompetitiveInsightsManageAddModalStateOption[];
}

export const COMPETITIVE_INSIGHTS_MANAGE_ADD_MODAL_CONFIGURATION_OPTIONS_QUERY_KEY_PREFIX = "competitive-insights-manage-add-modal-configuration-options";

export const useCompetitiveInsightsManageAddModalConfigurationOptions = (
  clientId: number | null,
  isMedicaid: boolean
): CompetitiveInsightsManageAddModalConfigurationOptionsResult => {
  const benchmarkingMedicaidService = useMemo(() => new BenchmarkingMedicaidService(), []);
  const benchmarkingQHPService = useMemo(() => new BenchmarkingQHPService(), []);

  const queryResult = useQuery<CompetitiveInsightsManageAddModalOptionsModel>({
    enabled: clientId !== null,
    queryFn: ({ signal }) => (isMedicaid
      ? benchmarkingMedicaidService.getAddConfigurationOptions(clientId as number, signal)
      : benchmarkingQHPService.getAddConfigurationOptions(clientId as number, signal)),
    queryKey: [COMPETITIVE_INSIGHTS_MANAGE_ADD_MODAL_CONFIGURATION_OPTIONS_QUERY_KEY_PREFIX, clientId, isMedicaid],
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    isLoading: queryResult.isFetching,
    programs: queryResult.data?.programs ?? [],
    projects: queryResult.data?.projects ?? [],
    states: queryResult.data?.states ?? [],
  };
}

export default useCompetitiveInsightsManageAddModalConfigurationOptions;
